import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Table } from '../../components/ui/table';
import { Select } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Users, Shield, Edit, Ban } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ROLES = [
  { value: 'super_admin', label: 'Super Admin', color: 'bg-[hsl(var(--destructive))] text-white' },
  { value: 'company_admin', label: 'Company Admin', color: 'bg-[hsl(var(--accent))] text-white' },
  { value: 'manager', label: 'Manager', color: 'bg-[hsl(var(--info))] text-white' },
  { value: 'viewer', label: 'Viewer', color: 'bg-[hsl(var(--muted))] text-white' },
  { value: 'operator', label: 'Operator', color: 'bg-[hsl(var(--warning))] text-white' }
];

export default function AdminUsersPage() {
  const [users, setUsers] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newRole, setNewRole] = useState('');
  const [selectedCompany, setSelectedCompany] = useState('');
  const [showInviteDialog, setShowInviteDialog] = useState(false);
  const [inviteFormData, setInviteFormData] = useState({
    email: '',
    company_id: '',
    role: 'viewer'
  });
  const [inviteLink, setInviteLink] = useState('');
  
  // Pagination state for users
  const [userPage, setUserPage] = useState(0);
  const [userLimit, setUserLimit] = useState(50);
  const [userTotal, setUserTotal] = useState(0);
  
  // Pagination state for companies
  const [companyPage, setCompanyPage] = useState(0);
  const [companyLimit, setCompanyLimit] = useState(50);
  const [companyTotal, setCompanyTotal] = useState(0);

  useEffect(() => {
    fetchUsers();
    fetchCompanies();
    fetchInvitations();
  }, [userPage, companyPage]);

  const fetchInvitations = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/invitations`, {
        withCredentials: true
      });
      setInvitations(response.data);
    } catch (error) {
      console.error('[AdminUsers] Error fetching invitations:', error);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/companies?skip=${companyPage * companyLimit}&limit=${companyLimit}`, {
        withCredentials: true
      });
      if (response.data.companies) {
        setCompanies(response.data.companies);
        setCompanyTotal(response.data.total);
      } else {
        // Backward compatibility if backend doesn't return paginated response
        setCompanies(response.data);
      }
    } catch (error) {
      console.error('[AdminUsers] Error fetching companies:', error);
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/users?skip=${userPage * userLimit}&limit=${userLimit}`, {
        withCredentials: true
      });
      if (response.data.users) {
        setUsers(response.data.users);
        setUserTotal(response.data.total);
      } else {
        // Backward compatibility if backend doesn't return paginated response
        setUsers(response.data);
      }
    } catch (error) {
      console.error('[AdminUsers] Error fetching:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleAssignRole = async () => {
    if (!selectedUser || !newRole) return;

    try {
      // Assign role
      await axios.post(
        `${BACKEND_URL}/api/admin/users/${selectedUser.id}/role`,
        { role: newRole },
        { withCredentials: true }
      );
      
      // Assign company if changed
      if (selectedCompany !== (selectedUser.company_id || '')) {
        await axios.post(
          `${BACKEND_URL}/api/admin/users/${selectedUser.id}/company`,
          { company_id: selectedCompany || null },
          { withCredentials: true }
        );
      }
      
      toast.success('User updated successfully!');
      setSelectedUser(null);
      setNewRole('');
      setSelectedCompany('');
      await fetchUsers();
    } catch (error) {
      console.error('[AdminUsers] Error updating user:', error);
      toast.error('Failed to update user');
    }
  };

  const handleSendInvite = async () => {
    if (!inviteFormData.email) {
      toast.error('Email is required');
      return;
    }

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/admin/invitations/create`,
        inviteFormData,
        { withCredentials: true }
      );
      
      setInviteLink(response.data.invite_link);
      toast.success('Invitation created! Copy the link below.');
      setInviteFormData({ email: '', company_id: '', role: 'viewer' });
      await fetchInvitations();
    } catch (error) {
      console.error('[AdminUsers] Error creating invitation:', error);
      toast.error(error.response?.data?.detail || 'Failed to create invitation');
    }
  };

  const handleCancelInvite = async (invitationId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/invitations/${invitationId}`, {
        withCredentials: true
      });
      toast.success('Invitation cancelled');
      await fetchInvitations();
    } catch (error) {
      console.error('[AdminUsers] Error cancelling invitation:', error);
      toast.error('Failed to cancel invitation');
    }
  };

  const copyInviteLink = () => {
    navigator.clipboard.writeText(inviteLink);
    toast.success('Invite link copied to clipboard!');
  };

  const getRoleBadge = (role) => {
    const roleConfig = ROLES.find(r => r.value === role) || ROLES[3];
    return <Badge className={roleConfig.color}>{roleConfig.label}</Badge>;
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center min-h-screen">
        <Users className="h-12 w-12 animate-pulse text-[hsl(var(--muted-foreground))]" />
      </div>
    );
  }

  return (
    <div className="p-8 space-y-8" data-testid="admin-users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-semibold" style={{ fontFamily: 'var(--font-display)' }}>
            User Management
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] mt-1">Manage user roles and permissions</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={() => setShowInviteDialog(true)}
            className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
            data-testid="invite-user-btn"
          >
            <Users className="h-4 w-4 mr-2" />
            Invite User
          </Button>
          <Badge variant="outline" className="text-sm">
            {users.length} Total Users
          </Badge>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-4 font-semibold">Name</th>
                  <th className="text-left p-4 font-semibold">Email</th>
                  <th className="text-left p-4 font-semibold">Role</th>
                  <th className="text-left p-4 font-semibold">Status</th>
                  <th className="text-left p-4 font-semibold">Company</th>
                  <th className="text-right p-4 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} className="border-b hover:bg-[hsl(var(--secondary))]" data-testid={`user-row-${user.id}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        {user.picture && (
                          <img src={user.picture} alt={user.name} className="h-8 w-8 rounded-full" />
                        )}
                        <span className="font-medium">{user.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-[hsl(var(--muted-foreground))]">{user.email}</td>
                    <td className="p-4">{getRoleBadge(user.role || 'viewer')}</td>
                    <td className="p-4">
                      <Badge variant={user.status === 'active' ? 'default' : 'destructive'}>
                        {user.status || 'active'}
                      </Badge>
                    </td>
                    <td className="p-4 text-[hsl(var(--muted-foreground))]">
                      {user.company_name || 'No Company'}
                    </td>
                    <td className="p-4">
                      <div className="flex justify-end gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setSelectedUser(user);
                            setNewRole(user.role || 'viewer');
                            setSelectedCompany(user.company_id || '');
                          }}
                          data-testid={`edit-role-${user.id}`}
                        >
                          <Edit className="h-4 w-4 mr-1" />
                          Edit Role
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          
          {/* Pagination Controls */}
          {userTotal > 0 && (
            <div className="flex items-center justify-between p-4 border-t">
              <div className="text-sm text-[hsl(var(--muted-foreground))]">
                Showing {userPage * userLimit + 1} to {Math.min((userPage + 1) * userLimit, userTotal)} of {userTotal} users
              </div>
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setUserPage(Math.max(0, userPage - 1))}
                  disabled={userPage === 0}
                  data-testid="users-prev-page"
                >
                  Previous
                </Button>
                <div className="flex items-center gap-2 px-3">
                  <span className="text-sm">Page {userPage + 1} of {Math.ceil(userTotal / userLimit)}</span>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setUserPage(userPage + 1)}
                  disabled={(userPage + 1) * userLimit >= userTotal}
                  data-testid="users-next-page"
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Role Assignment Dialog */}
      <Dialog open={!!selectedUser} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <DialogContent data-testid="assign-role-dialog">
          <DialogHeader>
            <DialogTitle>Assign Role - {selectedUser?.name}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email</label>
              <p className="text-sm text-[hsl(var(--muted-foreground))]">{selectedUser?.email}</p>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Select Role</label>
              <select
                className="w-full p-2 border rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                value={newRole}
                onChange={(e) => setNewRole(e.target.value)}
                data-testid="role-select"
              >
                {ROLES.map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign to Company</label>
              <select
                className="w-full p-2 border rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                value={selectedCompany}
                onChange={(e) => setSelectedCompany(e.target.value)}
                data-testid="company-select"
              >
                <option value="">No Company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role Description</label>
              <div className="text-xs text-[hsl(var(--muted-foreground))] space-y-1">
                {newRole === 'super_admin' && <p>• Full platform control, manage all companies and users</p>}
                {newRole === 'company_admin' && <p>• Manage company users and settings</p>}
                {newRole === 'manager' && <p>• Can view and edit data within permissions</p>}
                {newRole === 'viewer' && <p>• Read-only access to assigned modules</p>}
                {newRole === 'operator' && <p>• Specific module access for operations</p>}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSelectedUser(null)}>Cancel</Button>
            <Button
              onClick={handleAssignRole}
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
              data-testid="save-role-btn"
            >
              Assign Role
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invite User Dialog */}
      <Dialog open={showInviteDialog} onOpenChange={setShowInviteDialog}>
        <DialogContent data-testid="invite-dialog">
          <DialogHeader>
            <DialogTitle>Invite New User</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Email Address</label>
              <Input
                type="email"
                placeholder="user@company.com"
                value={inviteFormData.email}
                onChange={(e) => setInviteFormData({...inviteFormData, email: e.target.value})}
                data-testid="invite-email-input"
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Assign to Company</label>
              <select
                className="w-full p-2 border rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                value={inviteFormData.company_id}
                onChange={(e) => setInviteFormData({...inviteFormData, company_id: e.target.value})}
                data-testid="invite-company-select"
              >
                <option value="">No Company</option>
                {companies.map(company => (
                  <option key={company.id} value={company.id}>{company.name}</option>
                ))}
              </select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Role</label>
              <select
                className="w-full p-2 border rounded-md bg-[hsl(var(--background))] text-[hsl(var(--foreground))]"
                value={inviteFormData.role}
                onChange={(e) => setInviteFormData({...inviteFormData, role: e.target.value})}
                data-testid="invite-role-select"
              >
                {ROLES.filter(r => r.value !== 'super_admin').map(role => (
                  <option key={role.value} value={role.value}>{role.label}</option>
                ))}
              </select>
            </div>
            {inviteLink && (
              <div className="space-y-2 p-4 bg-[hsl(var(--accent))]/10 border border-[hsl(var(--accent))] rounded-md">
                <label className="text-sm font-medium">Invite Link (expires in 7 days)</label>
                <div className="flex gap-2">
                  <Input
                    value={inviteLink}
                    readOnly
                    className="font-mono text-xs"
                    data-testid="invite-link"
                  />
                  <Button onClick={copyInviteLink} size="sm">Copy</Button>
                </div>
                <p className="text-xs text-[hsl(var(--muted-foreground))]">
                  Share this link with the user. They will be auto-assigned to the company and role when they sign in.
                </p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowInviteDialog(false);
              setInviteLink('');
            }}>Close</Button>
            <Button
              onClick={handleSendInvite}
              className="bg-[hsl(var(--accent))] text-[hsl(var(--accent-foreground))]"
              data-testid="send-invite-btn"
            >
              Generate Invite
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Invitations */}
      {invitations.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle>Pending Invitations</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {invitations.filter(inv => inv.status === 'pending').map((invitation) => (
                <div key={invitation.id} className="flex items-center justify-between p-3 border rounded-md" data-testid={`invitation-${invitation.id}`}>
                  <div>
                    <div className="font-medium">{invitation.email}</div>
                    <div className="text-xs text-[hsl(var(--muted-foreground))]">
                      Role: {ROLES.find(r => r.value === invitation.role)?.label || invitation.role} | 
                      Company: {invitation.company_name || 'No Company'} |
                      Expires: {new Date(invitation.expires_at).toLocaleDateString()}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[hsl(var(--destructive))]"
                    onClick={() => handleCancelInvite(invitation.id)}
                    data-testid={`cancel-invite-${invitation.id}`}
                  >
                    Cancel
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
